import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TooltipProps } from '@magicschool/spellbook/components/Tooltip';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as ModalPrimitive from 'radix-ui/dialog';
import type { ComponentProps } from 'react';

export const ModalTriggerSlot = Slot<ComponentProps<typeof ModalPrimitive.Trigger>>({
  displayName: 'Modal.Trigger',
  preventOverride: true,
  renderDefault: ({ asChild = true, ...props }) => (
    <ModalPrimitive.Trigger asChild={asChild} data-spellbook-name="modal-trigger" {...props} />
  ),
});

export const ModalTooltipSlot = Slot<
  Omit<TooltipProps, 'asToggleTip'> & {
    'data-testid'?: string;
    'aria-describedby'?: string;
    'aria-labelledby'?: string;
  }
>({
  displayName: 'Modal.Tooltip',
  spellbookName: false,
  preventOverride: true,
  renderDefault: ({
    className,
    children,
    side = 'top',
    align = 'center',
    'data-testid': dataTestId,
    'aria-describedby': ariaDescribedBy,
    'aria-labelledby': ariaLabelledBy,
    ...props
  }) => {
    const { slots } = useSlots(children, {
      tooltipContent: { Component: ModalTooltipContentSlot },
      trigger: { Component: ModalTriggerSlot, props: { 'data-testid': dataTestId } },
    });

    return (
      <Tooltip className={cn('group', className)} side={side} align={align} asToggleTip={false} {...props}>
        <Tooltip.Trigger aria-describedby={ariaDescribedBy} aria-labelledby={ariaLabelledBy}>
          {slots.trigger}
        </Tooltip.Trigger>
        {slots.tooltipContent && <Tooltip.Content>{slots.tooltipContent}</Tooltip.Content>}
      </Tooltip>
    );
  },
});

export const ModalTooltipContentSlot = Slot<TextProps>({
  displayName: 'Modal.TooltipContent',
  preventOverride: true,
  renderDefault: ({ ...props }) => <Text data-spellbook-name="modal-tooltip-content" variant="caption-1" element="span" {...props} />,
});

/**
 * @TODO [SPE-47] Rename `Header` Slots to `Heading`
 * https://linear.app/magicschool/issue/SPE-47/rename-header-slots-to-heading
 */
export const ModalHeaderSlot = Slot<TextProps>({
  displayName: 'Modal.Header',
  preventOverride: true,
  renderDefault: (props) => <Text element="h2" variant="heading-3" {...props} />,
});

export const ModalDescriptionSlot = Slot<TextProps>({
  displayName: 'Modal.Description',
  preventOverride: true,
  renderDefault: (props) => <Text color="default-soft" {...props} />,
});

export const ModalHeaderAddOnSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Modal.HeaderAddOn',
  preventOverride: true,
  renderDefault: ({ className, ...props }) => (
    <div data-spellbook-name="modal-header-add-on" className={cn('flex items-center justify-between gap-2', className)} {...props} />
  ),
});

export const ModalContentSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Modal.Content',
  renderDefault: ({ className, ...props }) => (
    <div
      className={cn('px-6 pb-6 flex flex-col gap-4 w-full sm:grow overflow-y-auto', className)}
      data-spellbook-name="modal-content"
      {...props}
    />
  ),
});

export const ModalFooterSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Modal.Footer',
  renderDefault: ({ className, ...props }) => (
    <div className={cn('flex flex-col gap-4 sm:flex-row-reverse px-6 py-4', className)} data-spellbook-name="modal-footer" {...props} />
  ),
});

export const ModalCloseSlot = Slot<ComponentProps<typeof ModalPrimitive.Close>>({
  displayName: 'Modal.Close',
  preventOverride: true,
  renderDefault: (props) => <ModalPrimitive.Close data-spellbook-name="modal-close" {...props} />,
});

export const ModalHeaderActionsSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Modal.HeaderActions',
  preventOverride: true,
  renderDefault: ({ className, ...props }) => (
    <div
      className={cn(
        [
          'flex',
          'gap-2',
          'justify-center',
          'sm:justify-end',
          'px-6',
          'absolute',
          '[&_*]:grow',
          'top-18',
          'sm:px-0',
          'sm:top-4',
          'left-0',
          'ltr:sm:left-auto',
          'rtl:sm:right-auto',
          'right-0',
          'ltr:sm:right-15',
          'rtl:sm:left-15',
          'py-1.5',
          'sm:py-0',
        ],
        className,
      )}
      data-spellbook-name="modal-header-actions"
      {...props}
    />
  ),
});
